/* eslint-disable no-nested-ternary */
// Payments-tab advanced table's Actions column — the exact same actions the
// old PaymentCard offered, ported onto a single row cell: Mark as paid /
// Send link / Copy payment link / Resend ▾ (channel-icons + readiness
// disabling) / ⋯ menu (View details is handled by the grid's own row-expand
// chevron, not duplicated here; Mark as paid for link payments with the
// cancel-link warning; Mark as unpaid when paid_manually; Refund).
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch, useSelector } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { extractApiError } from 'src/utils/extractApiError';
import { getStripe, selectStripeIsLiveOrPending } from 'src/redux/slices/practiceIntegration';
import { derivePaymentCardStatus, RECEIVED_MODES } from './paymentCardStatus';
import { resendPaymentLink, getEligibleWhatsappTemplates, markPaymentUnpaid } from './paymentLinkApi';
import { getPaymentLinkChannelReadiness } from './paymentLinkReadiness';
import CreateSendPaymentLinkDialog from './CreateSendPaymentLinkDialog';
import MarkAsPaidDialog from './MarkAsPaidDialog';
import { RefundPaymentPopup, ConfirmDialog } from '../../../components/dialogPopUp';
import { leadResendChannels, paymentMethodLabel, WHATSAPP_ICON_COLOR } from './paymentPresentation';
import { CHANNEL_ICON } from './deliveryChips';
import usePaymentSessions, { sessionsCache, cacheKey } from './usePaymentSessions';

export default function PaymentActionsCell({ payment, leadDetail, practiceId, leadId, treatmentId, onChanged }) {
    const status = derivePaymentCardStatus(payment);
    const isLinkPayment = ['awaiting', 'expired', 'failed'].includes(status);
    // Backend treats both as manual/reversible: a payment explicitly marked
    // paid via the mark-paid endpoint (paid_manually), and any payment whose
    // recorded mode is one of the Take Payment dialog's manual modes (the
    // 'received' status is itself only ever reached via a received mode —
    // see derivePaymentCardStatus — but paid_manually can also appear on a
    // 'paid' row created by an old link that was later marked paid by phone).
    const canMarkUnpaid = Boolean(payment?.paid_manually) || RECEIVED_MODES.includes(payment?.mode_of_payment);
    // Refund is a Stripe refund — it must only ever show for a payment
    // actually paid through a Stripe checkout session, never for a manual/
    // received-mode payment (PHONE/INPRACTICE/BANK_TRANSFER/OTHER) or one
    // marked paid via the mark-paid endpoint (paid_manually) — even though
    // both of those can carry status 'paid' (paid_manually always does).
    // There's no Stripe payment behind either to refund.
    const canRefund = !payment?.paid_manually && paymentMethodLabel(payment) === 'Stripe link';

    const [resendAnchor, setResendAnchor] = useState(null);
    const [moreAnchor, setMoreAnchor] = useState(null);
    const [resending, setResending] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [refundOpen, setRefundOpen] = useState(false);
    const [markPaidOpen, setMarkPaidOpen] = useState(false);
    const [markUnpaidOpen, setMarkUnpaidOpen] = useState(false);
    const [markingUnpaid, setMarkingUnpaid] = useState(false);

    const { latestSession, refresh: refreshSessions } = usePaymentSessions(payment, {
        practiceId, leadId, treatmentId, eager: isLinkPayment,
    });

    // ─── Resend-menu / Send-link channel readiness (mirrors backend
    // _validate_prerequisites) — only needed for 'awaiting' (Resend menu) and
    // 'noLink' (Send link gating) cards.
    const practiceSettings = useSelector((state) => {
        const loaded = state.practice?.practice;
        if (practiceId != null && loaded?.id != null && String(loaded.id) !== String(practiceId)) {
            return undefined;
        }
        return loaded?.settings;
    });
    const stripeConnected = useSelector(selectStripeIsLiveOrPending);
    const [whatsappEligibility, setWhatsappEligibility] = useState(null);

    useEffect(() => {
        if ((status !== 'awaiting' && status !== 'noLink') || !practiceId) return undefined;
        // silent: true — background readiness probe, not a user action; see getStripe.
        dispatch(getStripe(practiceId, { silent: true }));
        if (status !== 'awaiting') return undefined;
        let cancelled = false;
        Promise.resolve(getEligibleWhatsappTemplates(practiceId))
            .then((res) => {
                if (cancelled) return;
                if (res?.status === 200 && res.data) {
                    setWhatsappEligibility({
                        connected: Boolean(res.data.connected),
                        results: Array.isArray(res.data.results) ? res.data.results : [],
                    });
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, practiceId]);

    const { channels: availableResendChannels, email: resendEmail, phone: resendPhone } = leadResendChannels(leadDetail);
    const resendReadiness = getPaymentLinkChannelReadiness({
        leadDetail,
        hasEmailOnFile: Boolean(resendEmail),
        hasPhoneOnFile: Boolean(resendPhone),
        practiceSettings,
        stripeConnected,
        whatsappEligibility,
    });

    const refresh = useCallback(() => {
        refreshSessions();
        if (onChanged) onChanged();
    }, [refreshSessions, onChanged]);

    const handleCopyLink = useCallback(async () => {
        if (!latestSession?.url) return;
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                await navigator.clipboard.writeText(latestSession.url);
            }
            dispatch(updateError({ success: true, message: 'Payment link copied' }));
        } catch {
            dispatch(updateError({ success: false, message: 'Could not copy payment link' }));
        }
        setMoreAnchor(null);
    }, [latestSession]);

    const handleResend = useCallback(
        async (channels) => {
            setResendAnchor(null);
            if (!channels || channels.length === 0 || !payment?.id) return;
            setResending(true);
            try {
                const { email, phone } = leadResendChannels(leadDetail);
                const payload = { channels };
                if (channels.includes('email') && email) payload.email = email;
                if (channels.includes('whatsapp') && phone) payload.phone = phone;
                const res = await resendPaymentLink(practiceId, leadId, treatmentId, payment.id, payload);
                if (res?.ok && res.status === 201) {
                    dispatch(updateError({ success: true, message: 'Payment link resent' }));
                    refresh();
                } else if (res?.status === 409) {
                    dispatch(updateError({ success: false, message: res?.error || 'This payment link has expired.' }));
                    refresh();
                } else {
                    dispatch(updateError({ success: false, message: extractApiError(res, 'Could not resend payment link.') }));
                }
            } catch {
                dispatch(updateError({ success: false, message: 'Could not resend payment link.' }));
            } finally {
                setResending(false);
            }
        },
        [payment, leadDetail, practiceId, leadId, treatmentId, refresh]
    );

    const handleMarkPaidSuccess = useCallback(
        (updatedPayment) => {
            dispatch(updateError({ success: true, message: 'Payment marked as paid' }));
            if (Array.isArray(updatedPayment?.warnings) && updatedPayment.warnings.includes('stripe_link_not_expired')) {
                dispatch(updateError({
                    success: 'warning',
                    message: "Marked as paid, but the Stripe link couldn't be cancelled — please check Stripe",
                }));
            }
            refresh();
        },
        [refresh]
    );

    const handleMarkPaidError = useCallback((message) => {
        dispatch(updateError({ success: false, message: message || 'Could not mark this payment as paid.' }));
    }, []);

    const handleConfirmMarkUnpaid = useCallback(async () => {
        if (!payment?.id) return;
        setMarkingUnpaid(true);
        try {
            const res = await markPaymentUnpaid(practiceId, leadId, treatmentId, payment.id);
            if (res?.ok) {
                dispatch(updateError({ success: true, message: 'Payment marked as unpaid' }));
                setMarkUnpaidOpen(false);
                refresh();
            } else {
                dispatch(updateError({ success: false, message: extractApiError(res, 'Could not mark this payment as unpaid.') }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Could not mark this payment as unpaid.' }));
        } finally {
            setMarkingUnpaid(false);
        }
    }, [payment, practiceId, leadId, treatmentId, refresh]);

    const handleOpenRefund = useCallback(async () => {
        const key = cacheKey(practiceId, leadId, treatmentId, payment?.id);
        if (!sessionsCache.has(key)) await refreshSessions();
        setRefundOpen(true);
    }, [practiceId, leadId, treatmentId, payment, refreshSessions]);

    return (
        <Stack
            direction="row"
            spacing={0.75}
            flexWrap="nowrap"
            alignItems="center"
            sx={{ py: 0.5, width: '100%', overflow: 'hidden' }}
        >
            {status === 'awaiting' && (
                <>
                    {latestSession?.url && (
                        <Button size="small" variant="outlined" onClick={handleCopyLink} data-testid={`copy-link-${payment?.id}`}>
                            Copy payment link
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={resending || availableResendChannels.length === 0}
                        onClick={(e) => setResendAnchor(e.currentTarget)}
                        data-testid={`resend-menu-btn-${payment?.id}`}
                    >
                        {resending ? <CircularProgress size={14} /> : 'Resend'} <Iconify icon="eva:chevron-down-fill" width={16} />
                    </Button>
                    <Menu anchorEl={resendAnchor} open={Boolean(resendAnchor)} onClose={() => setResendAnchor(null)}>
                        {availableResendChannels.includes('email') && (
                            <Tooltip title={resendReadiness.email.blocking ? resendReadiness.email.reason : ''} placement="right">
                                <span>
                                    <MenuItem
                                        onClick={() => handleResend(['email'])}
                                        disabled={resendReadiness.email.blocking}
                                        data-testid={`resend-email-${payment?.id}`}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <Iconify icon={CHANNEL_ICON.email} width={18} data-testid={`resend-icon-email-${payment?.id}`} />
                                        </ListItemIcon>
                                        <ListItemText>Email</ListItemText>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        )}
                        {availableResendChannels.includes('whatsapp') && (
                            <Tooltip title={resendReadiness.whatsapp.blocking ? resendReadiness.whatsapp.reason : ''} placement="right">
                                <span>
                                    <MenuItem
                                        onClick={() => handleResend(['whatsapp'])}
                                        disabled={resendReadiness.whatsapp.blocking}
                                        data-testid={`resend-whatsapp-${payment?.id}`}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <Iconify icon={CHANNEL_ICON.whatsapp} width={18} sx={{ color: WHATSAPP_ICON_COLOR }} data-testid={`resend-icon-whatsapp-${payment?.id}`} />
                                        </ListItemIcon>
                                        <ListItemText>WhatsApp</ListItemText>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        )}
                        {availableResendChannels.length > 1 && (
                            <MenuItem
                                onClick={() => handleResend(availableResendChannels)}
                                disabled={resendReadiness.email.blocking || resendReadiness.whatsapp.blocking}
                                data-testid={`resend-both-${payment?.id}`}
                            >
                                <ListItemIcon sx={{ minWidth: 32, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Iconify icon={CHANNEL_ICON.email} width={18} />
                                    <Iconify icon={CHANNEL_ICON.whatsapp} width={18} sx={{ color: WHATSAPP_ICON_COLOR }} />
                                </ListItemIcon>
                                <ListItemText>Email + WhatsApp</ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                    <Tooltip title="More options">
                        <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label="More options" data-testid={`more-menu-btn-${payment?.id}`}>
                            <Iconify icon="eva:more-horizontal-fill" width={18} />
                        </IconButton>
                    </Tooltip>
                    <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                        {latestSession?.url && <MenuItem onClick={handleCopyLink}>Copy Stripe link</MenuItem>}
                        <MenuItem onClick={() => { setMoreAnchor(null); setMarkPaidOpen(true); }} data-testid={`mark-paid-menu-item-${payment?.id}`}>
                            Mark as paid
                        </MenuItem>
                    </Menu>
                </>
            )}

            {(status === 'expired' || status === 'failed') && (
                <Button size="small" variant="contained" color="warning" onClick={() => setSendDialogOpen(true)} data-testid={`send-new-link-${payment?.id}`}>
                    Send new link
                </Button>
            )}

            {status === 'expired' && (
                <>
                    <Tooltip title="More options">
                        <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label="More options" data-testid={`more-menu-btn-${payment?.id}`}>
                            <Iconify icon="eva:more-horizontal-fill" width={18} />
                        </IconButton>
                    </Tooltip>
                    <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                        <MenuItem onClick={() => { setMoreAnchor(null); setMarkPaidOpen(true); }} data-testid={`mark-paid-menu-item-${payment?.id}`}>
                            Mark as paid
                        </MenuItem>
                    </Menu>
                </>
            )}

            {status === 'noLink' && (
                <>
                    <Button size="small" variant="contained" onClick={() => setMarkPaidOpen(true)} data-testid={`mark-paid-btn-${payment?.id}`}>
                        Mark as paid
                    </Button>
                    {stripeConnected !== false && (
                        <Tooltip
                            title={(resendReadiness.email.blocking && resendReadiness.whatsapp.blocking) ? (resendReadiness.email.reason || resendReadiness.whatsapp.reason) : ''}
                        >
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={resendReadiness.email.blocking && resendReadiness.whatsapp.blocking}
                                    onClick={() => setSendDialogOpen(true)}
                                    data-testid={`send-link-${payment?.id}`}
                                >
                                    Send link
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </>
            )}

            {status === 'paid' && (
                <>
                    {canRefund && (
                        <Button size="small" variant="outlined" color="inherit" onClick={handleOpenRefund} data-testid={`refund-btn-${payment?.id}`}>
                            Refund
                        </Button>
                    )}
                    {canMarkUnpaid && (
                        <>
                            <Tooltip title="More options">
                                <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label="More options" data-testid={`more-menu-btn-${payment?.id}`}>
                                    <Iconify icon="eva:more-horizontal-fill" width={18} />
                                </IconButton>
                            </Tooltip>
                            <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                                <MenuItem onClick={() => { setMoreAnchor(null); setMarkUnpaidOpen(true); }} data-testid={`mark-unpaid-menu-item-${payment?.id}`}>
                                    Mark as unpaid
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </>
            )}

            {status === 'received' && canMarkUnpaid && (
                <>
                    <Tooltip title="More options">
                        <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label="More options" data-testid={`more-menu-btn-${payment?.id}`}>
                            <Iconify icon="eva:more-horizontal-fill" width={18} />
                        </IconButton>
                    </Tooltip>
                    <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
                        <MenuItem onClick={() => { setMoreAnchor(null); setMarkUnpaidOpen(true); }} data-testid={`mark-unpaid-menu-item-${payment?.id}`}>
                            Mark as unpaid
                        </MenuItem>
                    </Menu>
                </>
            )}

            {sendDialogOpen && (
                <CreateSendPaymentLinkDialog
                    open={sendDialogOpen}
                    onClose={() => setSendDialogOpen(false)}
                    practiceId={practiceId}
                    leadId={leadId}
                    treatmentId={treatmentId}
                    payment={payment}
                    leadDetail={leadDetail}
                    replaceExpired={status === 'expired'}
                    onSuccess={refresh}
                />
            )}

            {refundOpen && (
                <RefundPaymentPopup
                    open={refundOpen}
                    onClose={() => { setRefundOpen(false); refresh(); }}
                    leadId={leadId}
                    treatmentId={treatmentId}
                    paymentId={payment?.id}
                    checkoutId={latestSession?.id}
                    paymentAmount={payment?.amount}
                />
            )}

            {markPaidOpen && (
                <MarkAsPaidDialog
                    open={markPaidOpen}
                    onClose={() => setMarkPaidOpen(false)}
                    payment={payment}
                    practiceId={practiceId}
                    leadId={leadId}
                    treatmentId={treatmentId}
                    cancelsOpenLink={isLinkPayment}
                    onSuccess={handleMarkPaidSuccess}
                    onError={handleMarkPaidError}
                />
            )}

            {markUnpaidOpen && (
                <ConfirmDialog
                    open={markUnpaidOpen}
                    onClose={() => setMarkUnpaidOpen(false)}
                    title="Mark as unpaid?"
                    content="This payment will be shown as unpaid again. This can't be undone automatically."
                    action={
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmMarkUnpaid}
                            disabled={markingUnpaid}
                            data-testid={`confirm-mark-unpaid-${payment?.id}`}
                        >
                            {markingUnpaid ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Mark as unpaid'}
                        </Button>
                    }
                />
            )}
        </Stack>
    );
}

PaymentActionsCell.propTypes = {
    payment: PropTypes.object.isRequired,
    leadDetail: PropTypes.object,
    practiceId: PropTypes.any,
    leadId: PropTypes.any,
    treatmentId: PropTypes.any,
    onChanged: PropTypes.func,
};

PaymentActionsCell.defaultProps = {
    leadDetail: null,
    practiceId: null,
    leadId: null,
    treatmentId: null,
    onChanged: null,
};
